import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Sparkles, Send, Loader2, ShieldCheck, AlertTriangle, Building2, Hash,
  Paintbrush, Palette, RefreshCw, ExternalLink, Lock, User, Bot, Lightbulb,
  Paperclip, Image as ImageIcon, Link2, X, FileText, File as FileIcon,
} from "lucide-react";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  { icon: Palette, label: "Palette plus chaleureuse", prompt: "Applique une palette plus chaleureuse : navy → terracotta, sky → ocre. Garde les contrastes AAA." },
  { icon: Paintbrush, label: "Hero plus moderne", prompt: "Modernise le design du Hero S2 : image à droite plein hauteur, texte à gauche avec un gros titre display, CTA primaire + badge 'Nouveau'." },
  { icon: Sparkles, label: "Animations au scroll", prompt: "Ajoute des animations fade-up douces (0.6s, stagger 100ms) sur les titres et cartes des sections S3 à S10 à leur apparition dans le viewport." },
  { icon: RefreshCw, label: "Typographie premium", prompt: "Remplace la typo display par 'Fraunces' pour un feeling éditorial, garde 'Inter' en body. Ajuste les interlignes et letter-spacing." },
];

export default function StudioAdmin() {
  const [pairing, setPairing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Bonjour ! Je suis le Studio IA de votre agence. Décrivez-moi ce que vous voulez changer dans le design de votre site — couleurs, typographies, disposition d'une section précise (référez-vous aux numéros S1..Sn), animations, etc. Je ne touche JAMAIS à vos contenus dynamiques ni à vos widgets.",
      ts: Date.now(),
    },
  ]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]); // {name, url, kind, mime_type, size, local?, uploading?}
  const [sending, setSending] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ name: "", url: "" });
  const scrollerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/agency/pairing-status")
      .then(r => setPairing(r.data))
      .catch(() => setPairing({ paired: false }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const prompt = (text ?? input).trim();
    if ((!prompt && attachments.length === 0) || sending) return;
    // Prevent sending while uploads pending
    if (attachments.some(a => a.uploading)) {
      toast.error("Un fichier est encore en upload…");
      return;
    }
    setInput("");
    const userMsg = { role: "user", content: prompt, ts: Date.now(), attachments: [...attachments] };
    setMessages(m => [...m, userMsg]);
    const sentAttachments = attachments.map(a => ({ name: a.name, url: a.url, kind: a.kind, mime_type: a.mime_type, size: a.size }));
    setAttachments([]);
    setSending(true);
    try {
      const { data } = await api.post("/agency/studio/chat", { prompt, attachments: sentAttachments });
      setMessages(m => [...m, { role: "assistant", content: data.reply || "(Réponse vide)", ts: Date.now(), meta: data }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Erreur inconnue";
      setMessages(m => [...m, { role: "assistant", content: detail, ts: Date.now(), error: true }]);
    } finally {
      setSending(false);
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} dépasse 10 MB`); continue; }
      const tmpId = Math.random().toString(36).slice(2);
      const local = {
        id: tmpId,
        name: f.name,
        kind: f.type.startsWith("image/") ? "image" : "file",
        mime_type: f.type,
        size: f.size,
        url: "",
        local: URL.createObjectURL(f),
        uploading: true,
      };
      setAttachments(a => [...a, local]);
      try {
        // Only images are uploaded to /cms/upload-image which converts to WebP.
        if (f.type.startsWith("image/")) {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("folder", "studio");
          const { data } = await api.post("/cms/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments(a => a.map(x => x.id === tmpId ? { ...x, url: data.url, uploading: false } : x));
        } else {
          // Non-image: we store it as a local blob URL reference only (not uploaded to media library).
          // The HUB will receive a description (name + mime) and the agency can ask for a proper share link.
          setAttachments(a => a.map(x => x.id === tmpId ? { ...x, url: local.local, uploading: false } : x));
          toast.info(`${f.name} ajouté localement (partagez le lien dans le prompt si besoin)`);
        }
      } catch (err) {
        toast.error(`Upload échoué : ${f.name}`);
        setAttachments(a => a.filter(x => x.id !== tmpId));
      }
    }
  };

  const addLink = () => {
    if (!linkForm.url.trim()) { toast.error("URL requise"); return; }
    setAttachments(a => [...a, {
      id: Math.random().toString(36).slice(2),
      name: linkForm.name || linkForm.url,
      url: linkForm.url,
      kind: "link",
    }]);
    setLinkForm({ name: "", url: "" });
    setLinkPickerOpen(false);
  };

  const removeAttachment = (id) => setAttachments(a => a.filter(x => x.id !== id));

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const imgs = [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) imgs.push(f);
      }
    }
    if (imgs.length > 0) {
      e.preventDefault();
      handleFiles(imgs);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">
        <Loader2 className="w-6 h-6 animate-spin inline" />
      </div>
    );
  }

  // ==== NOT PAIRED ====
  if (!pairing?.paired) {
    return (
      <div className="p-6 md:p-10 max-w-3xl" data-testid="studio-gate">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Studio IA design
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Studio IA — Thème &amp; design</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Modifiez le design visuel de votre site en langage naturel, via le chat IA de votre agence.
          </p>
        </header>

        <Card className="p-8 bg-gradient-to-br from-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white border-0 overflow-hidden relative">
          <div className="absolute -top-10 -right-10 opacity-10"><Sparkles className="w-40 h-40" /></div>
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center mb-5 backdrop-blur-sm">
              <Lock className="w-7 h-7" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">Réservé aux sites appairés</h2>
            <p className="text-white/85 text-sm leading-relaxed max-w-xl">
              Le Studio IA est activé dès que votre site est appairé à l'Accès Agence.
              L'agence peut alors modifier le design de votre thème (couleurs, typographies, disposition, animations)
              en langage naturel, tout en préservant 100% de vos données dynamiques (widgets, sections, produits, blog…).
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/admin/agency-pairing">
                <Button className="bg-white text-[color:var(--brand-navy)] hover:bg-white/90 font-bold" data-testid="studio-go-to-pairing">
                  <Building2 className="w-4 h-4 mr-1.5" /> Appairer le site à l'Accès Agence
                </Button>
              </Link>
              <a href="https://app.emergent.sh" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-white/10 hover:bg-white/15 text-white font-bold text-sm border border-white/20">
                En savoir plus <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </Card>

        <div className="mt-6 grid md:grid-cols-3 gap-3 text-sm">
          {[
            { icon: Palette, title: "Palette &amp; couleurs", desc: "Changez la charte graphique en gardant l'accessibilité AAA." },
            { icon: Paintbrush, title: "Refonte de sections", desc: "Ciblez une section (S1..Sn) et demandez un nouveau design." },
            { icon: Sparkles, title: "Animations &amp; motion", desc: "Ajoutez des micro-animations sur mesure au scroll et hover." },
          ].map((x, i) => (
            <Card key={i} className="p-4 border bg-gradient-to-br from-white to-[hsl(var(--muted))]/30">
              <x.icon className="w-5 h-5 text-[color:var(--brand-sky)] mb-2" />
              <div className="font-bold text-[color:var(--brand-navy)] text-sm mb-0.5" dangerouslySetInnerHTML={{ __html: x.title }} />
              <div className="text-xs text-[hsl(var(--muted-foreground))]" dangerouslySetInnerHTML={{ __html: x.desc }} />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ==== PAIRED — chat UI ====
  return (
    <div className="p-4 md:p-6 flex flex-col h-[calc(100vh-56px)] lg:h-[calc(100vh-48px)]" data-testid="studio-chat">
      <header className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[color:var(--brand-navy)] to-[color:var(--brand-sky)] flex items-center justify-center shrink-0 shadow-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)]">
              Studio IA <ShieldCheck className="w-3 h-3 text-emerald-600" />
            </div>
            <h1 className="font-display text-xl font-bold text-[color:var(--brand-navy)] leading-tight">Studio IA — Design &amp; thème</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-bold">Connecté à l'Accès Agence</Badge>
          <a href="/admin/sections" className="text-[11px] font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] inline-flex items-center gap-1">
            <Hash className="w-3 h-3" /> Voir les références S1..Sn
          </a>
        </div>
      </header>

      <Card className="p-3 mb-3 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Garanti :</strong> le Studio IA modifie uniquement les <strong>fichiers de thème</strong> (couleurs, tailles, dispositions).
            Vos contenus dynamiques (produits, sections on/off, articles de blog, widgets…) restent intacts et gérables depuis le back-office.
          </div>
        </div>
      </Card>

      {/* Quick prompts */}
      {messages.length <= 1 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1">
            <Lightbulb className="w-3 h-3" /> Suggestions
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {QUICK_PROMPTS.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => send(q.prompt)}
                className="text-left p-3 rounded-xl border bg-white hover:border-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/5 transition flex items-start gap-3"
                data-testid={`studio-quick-${i}`}
              >
                <q.icon className="w-4 h-4 text-[color:var(--brand-sky)] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm text-[color:var(--brand-navy)]">{q.label}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] line-clamp-2 mt-0.5">{q.prompt}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto space-y-4 pr-1"
        data-testid="studio-messages"
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.role}-${i}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-[color:var(--brand-navy)] text-white" : "bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white shadow"}`}>
              {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>
            <div className={`max-w-[75%] ${m.role === "user" ? "text-right" : ""}`}>
              <div className={`inline-block px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap text-left ${
                m.role === "user"
                  ? "bg-[color:var(--brand-navy)] text-white rounded-tr-md"
                  : m.error
                  ? "bg-red-50 border border-red-200 text-red-900 rounded-tl-md"
                  : "bg-white border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-tl-md"
              }`}>
                {m.content || <em className="opacity-60">(Pièces jointes)</em>}
                {m.attachments?.length > 0 && (
                  <div className={`mt-2 grid gap-2 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {m.attachments.map((a, j) => (
                      a.kind === "image" ? (
                        <a key={j} href={a.url} target="_blank" rel="noopener noreferrer" className="block">
                          <img src={a.url} alt={a.name} className="rounded-lg max-h-40 w-full object-cover border border-white/20" />
                        </a>
                      ) : a.kind === "link" ? (
                        <a key={j} href={a.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${m.role === "user" ? "bg-white/10 text-white hover:bg-white/20" : "bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted))]/80"} truncate`}>
                          <Link2 className="w-3 h-3 shrink-0" /> <span className="truncate">{a.name}</span>
                        </a>
                      ) : (
                        <div key={j} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${m.role === "user" ? "bg-white/10 text-white" : "bg-[hsl(var(--muted))]"}`}>
                          <FileIcon className="w-3 h-3 shrink-0" /> <span className="truncate">{a.name}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
              <div className={`text-[10px] text-[hsl(var(--muted-foreground))] mt-1 ${m.role === "user" ? "text-right" : ""}`}>
                {new Date(m.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] text-white flex items-center justify-center shadow">
              <Bot className="w-4 h-4" />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl rounded-tl-md bg-white border text-sm text-[hsl(var(--muted-foreground))]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Le Studio réfléchit…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="mt-4 border-t pt-3">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="studio-attachments-preview">
            {attachments.map(a => (
              <div key={a.id} className="group relative flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white text-xs max-w-[260px]">
                {a.kind === "image" ? (
                  <img src={a.local || a.url} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                ) : a.kind === "link" ? (
                  <Link2 className="w-3.5 h-3.5 text-[color:var(--brand-sky)] shrink-0" />
                ) : (
                  <FileIcon className="w-3.5 h-3.5 text-[color:var(--brand-navy)] shrink-0" />
                )}
                <span className="truncate font-semibold">{a.name}</span>
                {a.uploading && <Loader2 className="w-3 h-3 animate-spin text-[color:var(--brand-sky)] shrink-0" />}
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  className="ml-1 text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-red)] shrink-0"
                  aria-label="Retirer"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-9 h-9 rounded-full border hover:bg-[hsl(var(--muted))]/40 flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"
              title="Joindre une image ou un fichier"
              data-testid="studio-attach-file"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setLinkPickerOpen(true)}
              className="w-9 h-9 rounded-full border hover:bg-[hsl(var(--muted))]/40 flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"
              title="Ajouter un lien de référence"
              data-testid="studio-attach-link"
            >
              <Link2 className="w-4 h-4" />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.doc,.docx,.txt,.md"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="studio-file-input"
          />
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            onPaste={onPaste}
            placeholder="Décrivez ou collez une image d'inspiration. Glissez-déposez aussi directement ici…"
            rows={2}
            className="flex-1 resize-none"
            disabled={sending}
            data-testid="studio-input"
          />
          <Button
            type="button"
            onClick={() => send()}
            disabled={sending || (!input.trim() && attachments.length === 0) || attachments.some(a => a.uploading)}
            className="h-[56px] px-5 bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-sky)]"
            data-testid="studio-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2 flex items-center gap-3 flex-wrap">
          <span>Entrée = envoyer · Shift+Entrée = saut de ligne</span>
          <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Collez (Ctrl+V) ou déposez une image</span>
          <span>Max 10 MB par fichier</span>
        </div>
      </div>

      {/* Link picker modal */}
      {linkPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setLinkPickerOpen(false)}>
          <Card className="w-full max-w-md p-5 bg-white" onClick={(e) => e.stopPropagation()} data-testid="studio-link-modal">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="w-4 h-4 text-[color:var(--brand-sky)]" />
              <h3 className="font-bold text-[color:var(--brand-navy)]">Ajouter un lien de référence</h3>
            </div>
            <div className="space-y-2.5">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">URL</label>
                <input
                  type="url"
                  value={linkForm.url}
                  onChange={(e) => setLinkForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://dribbble.com/shots/..."
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  autoFocus
                  data-testid="studio-link-url"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Libellé (optionnel)</label>
                <input
                  type="text"
                  value={linkForm.name}
                  onChange={(e) => setLinkForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Inspiration Hero moderne"
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="studio-link-name"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setLinkPickerOpen(false)} className="text-xs">Annuler</Button>
              <Button size="sm" onClick={addLink} className="bg-[color:var(--brand-navy)] text-xs" data-testid="studio-link-add">Ajouter</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
